import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const TOOTH_PATTERN =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='150' height='150' viewBox='0 0 24 24'%3E%3Cg fill='none' stroke='%231259c3' stroke-opacity='0.10' stroke-width='1.4' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M12 3C8.2 3 5.5 5.8 5.5 9.4c0 2.4 1 3.9 1.6 6 .5 1.7.9 5.1 2.3 5.1 1.5 0 1-4.6 2.6-4.6s1.1 4.6 2.6 4.6c1.4 0 1.8-3.4 2.3-5.1.6-2.1 1.6-3.6 1.6-6C18.5 5.8 15.8 3 12 3z'/%3E%3C/g%3E%3C/svg%3E";

function EyeIcon({ off }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
      {off && <line x1="2" y1="2" x2="22" y2="22" />}
    </svg>
  );
}

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [form, setForm] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [hover, setHover] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const user = await login(form.username, form.password);
      const from = location.state?.from?.pathname;
      const fallback = user.role === 'superadmin' ? '/superadmin'
        : (user.role === 'admin' || user.role === 'secretaire') ? '/admin'
        : '/technicien';
      navigate(from && from !== '/login' ? from : fallback, { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Identifiants invalides');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = (name) => ({
    ...styles.input,
    borderColor: focused === name ? '#1259c3' : '#d7e0ec',
    boxShadow: focused === name ? '0 0 0 4px rgba(18, 89, 195, 0.12)' : 'none',
  });

  return (
    <div style={styles.page}>
      <div style={styles.wrap}>
        <div style={styles.card} className="odentis-rise">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 12 }}>
            <div style={{
              width: 44, height: 44, borderRadius: 12,
              background: 'linear-gradient(135deg, #2563eb, #10b981)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 22, color: '#fff', boxShadow: '0 4px 14px rgba(37,99,235,0.35)'
            }}>🦷</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.5px' }}>
              ODENTIS
            </div>
          </div>
          <div style={{ textAlign: 'center', fontSize: 13, color: '#64748b', marginBottom: 16 }}>
            Gestion Intelligente de Cabinet Dentaire
          </div>
          <div style={styles.divider} />
          {error && <div style={styles.error}>{error}</div>}
          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.field}>
              <label style={styles.label} htmlFor="username">Identifiant</label>
              <input
                id="username"
                type="text"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                onFocus={() => setFocused('username')}
                onBlur={() => setFocused(null)}
                placeholder="Votre identifiant"
                style={inputStyle('username')}
                required
                autoComplete="username"
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label} htmlFor="password">Mot de passe</label>
              <div style={styles.passwordWrap}>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  onFocus={() => setFocused('password')}
                  onBlur={() => setFocused(null)}
                  placeholder="Votre mot de passe"
                  style={{ ...inputStyle('password'), paddingRight: 48 }}
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                  onClick={() => setShowPassword((v) => !v)}
                  style={styles.eyeBtn}
                >
                  <EyeIcon off={showPassword} />
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              onMouseEnter={() => setHover(true)}
              onMouseLeave={() => setHover(false)}
              style={{
                ...styles.button,
                opacity: loading ? 0.75 : 1,
                transform: hover && !loading ? 'translateY(-1px)' : 'none',
                boxShadow: hover && !loading
                  ? '0 12px 26px rgba(18, 89, 195, 0.38)'
                  : '0 8px 18px rgba(18, 89, 195, 0.26)',
                cursor: loading ? 'not-allowed' : 'pointer',
              }}
            >
              {loading && <span className="odentis-spinner" />}
              {loading ? 'Connexion...' : 'Se connecter'}
            </button>
          </form>
        </div>
        <p style={styles.footer}>© 2026 ODENTIS — Gestion Cabinet Dentaire</p>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    backgroundColor: '#edf2f8',
    backgroundImage: `url("${TOOTH_PATTERN}"), radial-gradient(900px 480px at 85% -5%, rgba(18, 89, 195, 0.14), rgba(18, 89, 195, 0) 70%), radial-gradient(720px 440px at 6% 108%, rgba(10, 165, 160, 0.12), rgba(10, 165, 160, 0) 70%)`,
  },
  wrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    maxWidth: 410,
  },
  card: {
    width: '100%',
    background: '#fff',
    border: '1px solid #e2eaf4',
    borderRadius: 22,
    padding: '36px 36px 30px',
    boxShadow: '0 20px 55px rgba(11, 59, 143, 0.13)',
  },
  logo: { display: 'block', width: 185, maxWidth: '80%', height: 'auto', margin: '0 auto' },
  divider: { height: 1, background: '#e7edf5', margin: '26px 0 24px' },
  error: {
    background: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#dc2626',
    padding: '11px 14px',
    borderRadius: 12,
    marginBottom: 18,
    fontSize: 14,
    textAlign: 'center',
  },
  form: { display: 'flex', flexDirection: 'column', gap: 18 },
  field: { display: 'flex', flexDirection: 'column', gap: 7 },
  label: { fontSize: 13, fontWeight: 600, color: '#334155' },
  input: {
    width: '100%',
    padding: '13px 15px',
    border: '1.5px solid #d7e0ec',
    borderRadius: 12,
    fontSize: 15,
    outline: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    background: '#fbfdff',
  },
  passwordWrap: { position: 'relative' },
  eyeBtn: {
    position: 'absolute',
    right: 8,
    top: '50%',
    transform: 'translateY(-50%)',
    padding: 8,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
  },
  button: {
    marginTop: 6,
    minHeight: 52,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    background: 'linear-gradient(135deg, #1663c7 0%, #0b3b8f 100%)',
    color: '#fff',
    fontWeight: 700,
    fontSize: 16,
    borderRadius: 14,
    border: 'none',
    transition: 'transform 0.15s, box-shadow 0.2s, opacity 0.2s',
  },
  footer: { marginTop: 22, fontSize: 12, color: '#8a97a8', textAlign: 'center' },
};
